import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from "recharts";
import { TrendingUp, TrendingDown, Scale, Wheat, ArrowUpRight, ArrowDownRight } from "lucide-react";

export default function ComparativoSafras({ safras, custosAlocados, lancamentos }) {
  const [metrica, setMetrica] = useState("custo_ha");
  const [tipoGrafico, setTipoGrafico] = useState("bar");

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Calcular dados comparativos
  const dadosComparativos = useMemo(() => {
    return safras.map(safra => {
      // Custos alocados
      const custosAlocadosSafra = custosAlocados
        .filter(c => c.safra_id === safra.id)
        .reduce((acc, c) => acc + (c.valor || 0), 0);

      // Custos de lançamentos
      const custosLancamentos = lancamentos
        .filter(l => l.safra_id === safra.id && l.tipo === 'despesa')
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      const custoTotal = custosAlocadosSafra + custosLancamentos;

      // Receitas
      const receitas = lancamentos
        .filter(l => l.safra_id === safra.id && l.tipo === 'receita')
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      // Área e produção
      const area = safra.area_plantada || 0;
      const producao = safra.producao_real || safra.producao_esperada || 0;

      // Métricas
      const custoHa = area > 0 ? custoTotal / area : 0;
      const custoSaca = producao > 0 ? custoTotal / producao : 0;
      const produtividadeHa = area > 0 ? producao / area : 0;
      const receitaHa = area > 0 ? receitas / area : 0;
      const lucroHa = receitaHa - custoHa;
      const margem = receitas > 0 ? ((receitas - custoTotal) / receitas) * 100 : 0;

      return {
        id: safra.id,
        nome: safra.nome,
        cultura: safra.cultura,
        area,
        producao,
        custoTotal,
        receitas,
        custoHa,
        custoSaca,
        produtividadeHa,
        receitaHa,
        lucroHa,
        margem,
        lucro: receitas - custoTotal
      };
    }).filter(s => s.area > 0 || s.custoTotal > 0);
  }, [safras, custosAlocados, lancamentos]);

  // Dados para gráfico
  const dadosGrafico = useMemo(() => {
    const dados = dadosComparativos.map(s => ({
      nome: s.nome.length > 15 ? s.nome.substring(0, 15) + '...' : s.nome,
      custo_ha: s.custoHa,
      custo_saca: s.custoSaca,
      produtividade: s.produtividadeHa,
      receita_ha: s.receitaHa,
      lucro_ha: s.lucroHa,
      margem: s.margem
    }));
    return dados;
  }, [dadosComparativos]);

  // Médias
  const medias = useMemo(() => {
    if (dadosComparativos.length === 0) return null;
    const n = dadosComparativos.length;
    return {
      custoHa: dadosComparativos.reduce((a, s) => a + s.custoHa, 0) / n,
      custoSaca: dadosComparativos.reduce((a, s) => a + s.custoSaca, 0) / n,
      produtividadeHa: dadosComparativos.reduce((a, s) => a + s.produtividadeHa, 0) / n,
      margem: dadosComparativos.reduce((a, s) => a + s.margem, 0) / n
    };
  }, [dadosComparativos]);

  const metricasConfig = {
    custo_ha: { label: "Custo/ha", key: "custo_ha", color: "#ef4444", format: formatCurrency },
    custo_saca: { label: "Custo/Saca", key: "custo_saca", color: "#f97316", format: formatCurrency },
    produtividade: { label: "Produtividade (sc/ha)", key: "produtividade", color: "#22c55e", format: (v) => v.toFixed(1) },
    receita_ha: { label: "Receita/ha", key: "receita_ha", color: "#3b82f6", format: formatCurrency },
    lucro_ha: { label: "Lucro/ha", key: "lucro_ha", color: "#8b5cf6", format: formatCurrency },
    margem: { label: "Margem (%)", key: "margem", color: "#06b6d4", format: (v) => v.toFixed(1) + "%" }
  };

  const metricaAtual = metricasConfig[metrica];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-medium text-stone-800">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="text-sm" style={{ color: p.color }}>
              {metricaAtual.label}: {metricaAtual.format(p.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (dadosComparativos.length === 0) {
    return (
      <Card className="bg-white border-stone-200">
        <CardContent className="p-8 text-center">
          <Scale className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">Cadastre safras com área e custos para ver o comparativo</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumo de Médias */}
      {medias && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <p className="text-red-100 text-xs">Custo Médio/ha</p>
              <p className="text-xl font-bold">{formatCurrency(medias.custoHa)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <p className="text-orange-100 text-xs">Custo Médio/Saca</p>
              <p className="text-xl font-bold">{formatCurrency(medias.custoSaca)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <p className="text-green-100 text-xs">Produtividade Média</p>
              <p className="text-xl font-bold">{medias.produtividadeHa.toFixed(1)} sc/ha</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <p className="text-purple-100 text-xs">Margem Média</p>
              <p className="text-xl font-bold">{medias.margem.toFixed(1)}%</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Gráfico Comparativo */}
      <Card className="bg-white border-stone-200">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-purple-600" />
            Comparativo entre Safras
          </CardTitle>
          <div className="flex gap-2">
            <Select value={metrica} onValueChange={setMetrica}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custo_ha">Custo/ha</SelectItem>
                <SelectItem value="custo_saca">Custo/Saca</SelectItem>
                <SelectItem value="produtividade">Produtividade</SelectItem>
                <SelectItem value="receita_ha">Receita/ha</SelectItem>
                <SelectItem value="lucro_ha">Lucro/ha</SelectItem>
                <SelectItem value="margem">Margem %</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dadosGrafico} margin={{ top: 10, right: 30, left: 10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="nome" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                <YAxis 
                  tickFormatter={(v) => metrica === 'margem' || metrica === 'produtividade' ? v.toFixed(0) : new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey={metricaAtual.key} fill={metricaAtual.color} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Tabela Detalhada */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wheat className="w-5 h-5 text-amber-600" />
            Detalhamento por Safra
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-stone-200 bg-stone-50">
                  <th className="text-left py-3 px-3 font-semibold">Safra</th>
                  <th className="text-right py-3 px-3 font-semibold">Área</th>
                  <th className="text-right py-3 px-3 font-semibold">Produção</th>
                  <th className="text-right py-3 px-3 font-semibold text-red-600">Custo/ha</th>
                  <th className="text-right py-3 px-3 font-semibold text-orange-600">Custo/sc</th>
                  <th className="text-right py-3 px-3 font-semibold text-green-600">Prod./ha</th>
                  <th className="text-right py-3 px-3 font-semibold">Receita/ha</th>
                  <th className="text-right py-3 px-3 font-semibold">Lucro/ha</th>
                  <th className="text-center py-3 px-3 font-semibold">Margem</th>
                </tr>
              </thead>
              <tbody>
                {dadosComparativos.map((safra, index) => {
                  const melhorCusto = Math.min(...dadosComparativos.map(s => s.custoHa));
                  const piorCusto = Math.max(...dadosComparativos.map(s => s.custoHa));
                  const isMelhor = safra.custoHa === melhorCusto && dadosComparativos.length > 1;
                  const isPior = safra.custoHa === piorCusto && dadosComparativos.length > 1;

                  return (
                    <tr key={safra.id} className={`border-b border-stone-100 hover:bg-stone-50 ${isMelhor ? 'bg-green-50' : isPior ? 'bg-red-50' : ''}`}>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-medium text-stone-800">{safra.nome}</p>
                            <Badge variant="secondary" className="text-xs">{safra.cultura}</Badge>
                          </div>
                          {isMelhor && <ArrowDownRight className="w-4 h-4 text-green-600" />}
                          {isPior && <ArrowUpRight className="w-4 h-4 text-red-600" />}
                        </div>
                      </td>
                      <td className="text-right py-3 px-3">{safra.area.toFixed(1)} ha</td>
                      <td className="text-right py-3 px-3">{safra.producao.toFixed(0)} sc</td>
                      <td className="text-right py-3 px-3 font-medium text-red-600">{formatCurrency(safra.custoHa)}</td>
                      <td className="text-right py-3 px-3 font-medium text-orange-600">{formatCurrency(safra.custoSaca)}</td>
                      <td className="text-right py-3 px-3 font-medium text-green-600">{safra.produtividadeHa.toFixed(1)}</td>
                      <td className="text-right py-3 px-3">{formatCurrency(safra.receitaHa)}</td>
                      <td className={`text-right py-3 px-3 font-bold ${safra.lucroHa >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(safra.lucroHa)}
                      </td>
                      <td className="text-center py-3 px-3">
                        <Badge className={safra.margem >= 20 ? 'bg-green-100 text-green-700' : safra.margem >= 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                          {safra.margem.toFixed(1)}%
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}